<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { potter, pottePlanter, loadAllPottePlanter } from '../lib/stores';
  import { supabase } from '../lib/supabase';
  import { startPolling } from '../lib/polling';
  import { hentSensorHistorikk } from '../lib/sensorhistorikk';
  import { kasseVarsler, type VarselAlvor, type VarselHistorikkRad } from '../lib/varsler';
  import { visesFremhevet, tilkoblingsStatus } from '../lib/utils';
  import { iLysvindu } from '../lib/tid';
  import { kasseTanker, tankStatus } from '../lib/tanker';
  import { ppfdMaks, lysAvstander } from '../lib/settings';
  import { kassePpfd } from '../lib/lysavstand';
  import { simStore, hentSim, simHistorikk, effektivKasse } from '../lib/simulering';
  import { sensorOppsett, maskerAvlesning } from '../lib/sensorer';
  import type { Potte, PotteCommand, PotteSensorData, PottePlanteFull } from '../lib/database.types';
  import PotteKort from './PotteKort.svelte';
  import Sheet from './Sheet.svelte';
  import KasseInnstillinger from './KasseInnstillinger.svelte';

  let visNy = $state(false);

  type View = { name: 'oversikt' } | { name: 'potte'; potteId: string } | { name: 'katalog' };
  let { onNavigate }: { onNavigate: (v: View) => void } = $props();

  let commands = $state<Record<string, PotteCommand>>({});
  let sensors = $state<Record<string, PotteSensorData>>({});
  let historikk = $state<Record<string, VarselHistorikkRad[]>>({});
  let now = $state(new Date()); // driver sol-buen i kortene
  // Første henting ferdig? Til da viser kortene «Henter …» i stedet for
  // «Venter på første sensoravlesning» / «Ingen data ennå».
  let forsteHentet = $state(false);
  // Siste henting feilet (nett nede e.l.) — tallene er da de sist hentede.
  let nettFeil = $state(false);
  let henter = false;

  // Historikken (4 døgn, kun kolonnene varsel-motoren trenger) driver trend-
  // («holder ~X dager») og overvåt-varslene i feeden. Den endrer seg sakte
  // (én ny rad per 5 min), så vi henter den ved mount + hvert kvarter — ikke
  // i hver 10-sekunders refresh.
  let sistHistorikkHentet = 0;
  const HISTORIKK_REFRESH_MS = 15 * 60_000;
  const HISTORIKK_DOGN = 4; // trend trenger ≥6 t, overvåt 3 døgn + margin

  async function loadHistorikk() {
    sistHistorikkHentet = Date.now();
    const potteListe = get(potter).filter((p) => p.har_sensorer);
    const svar = await Promise.allSettled(
      potteListe.map((p) => hentSensorHistorikk(p.potte_id, HISTORIKK_DOGN)),
    );
    // En potte hvis henting feilet, beholder forrige historikk i stedet for å
    // miste trend-/overvåt-varslene til neste kvarter.
    const m: Record<string, VarselHistorikkRad[]> = { ...historikk };
    svar.forEach((res, idx) => {
      if (res.status === 'fulfilled') m[potteListe[idx]!.potte_id] = res.value;
    });
    historikk = m;
  }

  async function refresh() {
    if (henter) return; // tregt nett: ikke stable opp overlappende hentinger
    henter = true;
    now = new Date();
    try {
      // Nyeste avlesning hentes PER potte (limit 1 hver). En felles
      // «siste 50 rader»-spørring ville latt en aktiv potte skvise en
      // offline potte helt ut av lista etter noen timer — da så det ut som
      // den aldri hadde sendt data, akkurat når man trenger å se at noe er galt.
      const potteListe = get(potter);
      const [cmd, sensSvar] = await Promise.all([
        supabase.from('potte_commands').select('*'),
        Promise.all(
          potteListe.map((p) =>
            supabase
              .from('potte_sensor_data')
              .select('*')
              .eq('potte_id', p.potte_id)
              .order('registrert_at', { ascending: false })
              .limit(1)
              .maybeSingle(),
          ),
        ),
      ]);

      if (cmd.data) {
        const m: Record<string, PotteCommand> = {};
        for (const c of cmd.data) m[c.potte_id] = c;
        commands = m;
      }
      // Feilet hentingen for en potte (nett-blipp), beholdes forrige avlesning
      // — ellers ble kortet tømt og så ut som «venter på første avlesning».
      const m: Record<string, PotteSensorData> = {};
      sensSvar.forEach((res, idx) => {
        const id = potteListe[idx]!.potte_id;
        const forrige = sensors[id];
        if (res.data) m[id] = res.data;
        else if (res.error && forrige) m[id] = forrige;
      });
      sensors = m;
      nettFeil = !!cmd.error || sensSvar.some((r) => r.error);
      if (Date.now() - sistHistorikkHentet > HISTORIKK_REFRESH_MS) {
        loadHistorikk(); // bevisst uten await — feeden kan oppdatere i bakgrunnen
      }
    } catch (e) {
      console.error('Oversikt-refresh feilet:', e);
      nettFeil = true;
    } finally {
      henter = false;
      forsteHentet = true;
    }
  }

  // Intervallet startes med én gang (ikke etter en await) og hviler når
  // appen ligger i bakgrunnen — se lib/polling.ts.
  onMount(() => {
    refresh();
    loadAllPottePlanter();
    return startPolling(refresh, 10_000);
  });

  // Testmodus-simulator: bruker DEN ENE delte effektivKasse()-implementasjonen
  // (samme som PotteDetalj, se simulering.ts) — så en simulert kasse
  // forhåndsviser konsistent i BÅDE kortet og handlingsfeeden under, akkurat
  // som i detaljen. Aldri kopier denne logikken lokalt igjen.
  interface Kasse {
    potte: Potte;
    effektivPotte: Potte;
    effektivSensor: PotteSensorData | undefined;
    effektivePlanter: PottePlanteFull[];
    effektivHistorikk: VarselHistorikkRad[];
    /** Trend-prognose («holder ~X dager») til kortet, for tanken som går tom først. null = ukjent/fylles. */
    dagerIgjen: number | null;
    simAktiv: boolean;
  }
  const kasser = $derived.by((): Kasse[] =>
    $potter.map((p) => {
      // Sensorer kassa ikke har, nulles før alt annet (lib/sensorer.ts). Simulatoren
      // viser alle sensorer, så der er oppsettet «alle».
      const o = sensorOppsett(p);
      const e = effektivKasse(p, $pottePlanter[p.potte_id] ?? [], maskerAvlesning(sensors[p.potte_id], o), $simStore);
      // Sim-kasser får syntetisk historikk (samme kilde som detaljen) så trend-
      // og diagnose-funksjonene også kan forhåndsvises i testmodus.
      const hist = e.simAktiv
        ? simHistorikk(hentSim($simStore, p.potte_id), p)
        : (historikk[p.potte_id] ?? []).map((r) => maskerAvlesning(r, o));
      // Én tank i 1.0, to skuffer i stor: kortet viser den som går tom først.
      const dager = kasseTanker(e.potte, sensorOppsett(e.potte))
        .map((t) => tankStatus(t, e.sensor, hist).trend)
        .filter((tr) => tr.gyldig && tr.dagerIgjen !== null)
        .map((tr) => tr.dagerIgjen as number);
      return {
        potte: p,
        effektivPotte: e.potte,
        effektivSensor: e.sensor,
        effektivePlanter: e.planter,
        effektivHistorikk: hist,
        dagerIgjen: dager.length ? Math.min(...dager) : null,
        simAktiv: e.simAktiv,
      };
    }),
  );

  // Kasser i testmodus (rigger under bygging) dempes: kompakt liste nederst og
  // ingen varsler i feeden. Se visesFremhevet i lib/utils.ts.
  const fremhevede = $derived(kasser.filter(visesFremhevet));
  const testKasser = $derived(kasser.filter((k) => !visesFremhevet(k)));

  function testStatus(k: Kasse): string {
    const deler: string[] = [];
    const t = tilkoblingsStatus(k.potte.har_sensorer, sensors[k.potte.potte_id]?.registrert_at);
    if (t === 'tilkoblet') deler.push('tilkoblet');
    else if (t === 'frakoblet') deler.push('frakoblet');
    else if (t === 'ingen-data') deler.push(forsteHentet ? 'ingen data ennå' : 'henter …');
    const c = commands[k.potte.potte_id];
    if (c) deler.push(c.intensitet > 0 && iLysvindu(c.timer_on, c.timer_off, now) ? `lys ${c.intensitet} %` : 'lys av');
    const n = k.effektivePlanter.length;
    deler.push(n === 0 ? 'ingen planter' : `${n} ${n === 1 ? 'plante' : 'planter'}`);
    return deler.join(' · ');
  }

  // Handlingsfeed: hele regelverket bor i lib/varsler.ts (kasseVarsler) — REN,
  // testet logikk med maks ett varsel per kategori per kasse. Komponenten gjør
  // bare to ting: velger effektive data (sim eller ekte) og render.
  const varsler = $derived.by(() => {
    const ut: { potteId: string; navn: string; melding: string; alvor: VarselAlvor; ikon: string; simulert: boolean }[] = [];
    for (const { potte: p, effektivPotte, effektivSensor, effektivePlanter, effektivHistorikk, simAktiv } of fremhevede) {
      for (const v of kasseVarsler({
        potte: effektivPotte,
        sensor: effektivSensor,
        command: commands[p.potte_id],
        planter: effektivePlanter,
        historikk: effektivHistorikk,
        ppfdMaks: kassePpfd(p.potte_id, $lysAvstander, $ppfdMaks),
      })) {
        ut.push({ potteId: p.potte_id, navn: p.navn, ...v, simulert: simAktiv });
      }
    }
    return ut;
  });

  // Fire tydelige betydninger: rød = problem, gul = frakoblet, blå = oppgave
  // (næring), grønn = klar til høsting. Distinkte toner så feed-en er lesbar.
  const varselStil: Record<VarselAlvor, string> = {
    hoy: 'bg-rose/[0.12] border-rose/35',
    mid: 'bg-sun/[0.12] border-sun/35',
    gjøremål: 'bg-sky/[0.12] border-sky/35',
    positiv: 'bg-leaf/[0.12] border-leaf/35',
  };
</script>

<div class="max-w-[430px] md:max-w-4xl mx-auto w-full flex flex-col gap-3.5">
  <div class="mb-1">
    <h1 class="font-display text-[25px] font-semibold leading-tight tracking-tight">Mine blomsterkasser</h1>
    <p class="font-mono text-[11px] text-text-muted mt-1.5">
      {$potter.length} {$potter.length === 1 ? 'blomsterkasse' : 'blomsterkasser'}{#if testKasser.length > 0}&nbsp;· {testKasser.length} i testmodus{/if}
    </p>
  </div>

  {#if nettFeil}
    <div
      class="flex items-center gap-2 px-3 py-2 rounded-[12px] bg-sun/[0.08] border border-sun/25 text-[12px] text-sun leading-snug"
      role="status"
    >
      <span aria-hidden="true">📡</span>
      Får ikke kontakt med databasen — tallene under kan være gamle. Prøver igjen automatisk.
    </div>
  {/if}

  <!-- Advarsler (kun i appen — ingen telefon-varsler) -->
  {#if varsler.length > 0}
    <div class="flex flex-col gap-2">
      {#each varsler as v (v.potteId + v.melding)}
        <button
          class="flex items-center gap-2.5 w-full text-left px-3.5 py-2.5 rounded-xl border transition-all hover:brightness-110 {varselStil[
            v.alvor
          ]} {v.simulert ? '!border-dashed' : ''}"
          onclick={() => onNavigate({ name: 'potte', potteId: v.potteId })}
        >
          <span class="text-base leading-none">{v.ikon}</span>
          <span class="flex-1 text-sm leading-snug">
            <span class="font-semibold">{v.navn}:</span>
            <span class="text-text-muted">{v.melding}</span>
            {#if v.simulert}
              <span class="text-text-dim">· 🧪 simulert</span>
            {/if}
          </span>
          <span class="text-text-dim text-lg leading-none" aria-hidden="true">›</span>
        </button>
      {/each}
    </div>
  {/if}

  {#if $potter.length === 0}
    <div class="card p-10 text-center text-text-muted">
      <div class="text-3xl mb-3">🪴</div>
      <p>Ingen blomsterkasser registrert ennå</p>
      <p class="text-xs mt-1.5">Trykk «Legg til blomsterkasse» under for å sette opp den første.</p>
    </div>
  {:else}
    <!-- Mobil: én kolonne (uendret). Desktop: kortene i et 2-kolonners grid så
         den brede skjermen faktisk brukes i stedet for døde sidemarger. -->
    {#if fremhevede.length === 0}
      <div class="card px-5 py-6 text-center text-text-muted">
        <p class="text-sm">Ingen blomsterkasser i drift ennå.</p>
        <p class="text-xs mt-1">Kasser i testmodus ligger nederst. Sett en kasse i drift fra detaljsiden når den er klar.</p>
      </div>
    {/if}
    <div class="grid grid-cols-1 md:grid-cols-2 gap-3.5 items-start">
      {#each fremhevede as k (k.potte.id)}
        <PotteKort
          potte={k.effektivPotte}
          command={commands[k.potte.potte_id]}
          sensor={k.effektivSensor}
          planter={k.effektivePlanter}
          dagerIgjen={k.dagerIgjen}
          {now}
          onClick={() => onNavigate({ name: 'potte', potteId: k.potte.potte_id })}
          simulert={k.simAktiv}
          laster={!forsteHentet && !k.simAktiv}
        />
      {/each}
    </div>

    {#if testKasser.length > 0}
      <section class="flex flex-col gap-1.5 mt-1" aria-label="Blomsterkasser i testmodus">
        <h2 class="font-mono text-[10.5px] uppercase tracking-wider text-text-dim px-1">🧪 I testmodus</h2>
        {#each testKasser as k (k.potte.id)}
          <button
            class="flex items-center gap-3 w-full text-left px-3.5 py-2.5 rounded-xl border border-border bg-surface/40 opacity-75 hover:opacity-100 hover:bg-surface-raised/60 focus-visible:opacity-100 transition"
            onclick={() => onNavigate({ name: 'potte', potteId: k.potte.potte_id })}
          >
            <span class="flex-1 min-w-0">
              <span class="block text-sm font-medium truncate">{k.potte.navn}</span>
              <span class="block font-mono text-[11px] text-text-muted truncate">{testStatus(k)}</span>
            </span>
            <span class="text-text-dim text-lg leading-none" aria-hidden="true">›</span>
          </button>
        {/each}
      </section>
    {/if}
  {/if}

  <button
    class="flex items-center justify-center gap-2 w-full px-4 py-3.5 rounded-2xl border border-dashed border-border-strong text-text-muted hover:text-text hover:bg-surface-raised/40 transition-colors"
    onclick={() => (visNy = true)}
  >
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
    <span class="text-sm font-medium">Legg til blomsterkasse</span>
  </button>
</div>

<Sheet open={visNy} onClose={() => (visNy = false)} label="Ny blomsterkasse">
  {#if visNy}
    <KasseInnstillinger kasse={null} onLukk={() => (visNy = false)} onLagret={() => {}} />
  {/if}
</Sheet>
